import { CheckCircle2, ChevronDown, ChevronRight, Circle, Loader2, XCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const STATUS_ICON = {
  done: CheckCircle2,
  running: Loader2,
  blocked: XCircle,
  failed: XCircle,
  pending: Circle,
};

const STATUS_CLASS = {
  done: "text-emerald-600",
  running: "text-[var(--primary)] animate-spin",
  blocked: "text-amber-600",
  failed: "text-red-500",
  pending: "text-[var(--text-tertiary)]",
};

const DEFAULT_BUSY_STEPS = [
  { id: "understand", label: "Understanding your request", status: "running", detail: "Reading intent…" },
  { id: "plan", label: "Planning how to help", status: "pending", detail: "Choosing tools & modules" },
  { id: "work", label: "Working in your workspace", status: "pending", detail: "Applying changes when needed" },
];

export function inferBusySteps(message = "") {
  const lower = String(message || "").toLowerCase();
  if (
    lower.includes("what can you") ||
    lower.includes("how can you help") ||
    lower.includes("what do you do") ||
    lower.includes("completed") ||
    lower.includes("setup progress")
  ) {
    return [
      { id: "understand", label: "Understanding your question", status: "running", detail: "Capability / progress ask" },
      { id: "scan", label: "Scanning event setup checklist", status: "pending", detail: "What's done vs remaining" },
      { id: "reply", label: "Preparing a clear answer", status: "pending", detail: "Capabilities + next steps" },
    ];
  }
  if (lower.includes("ticket") || lower.includes("selling") || lower.includes("quota") || lower.includes("dashboard") || lower.includes("revenue")) {
    return [
      { id: "insights", label: "Building event dashboard", status: "running", detail: "Pulling live registration data" },
      { id: "summary", label: "Summarizing registrations & ticket sales", status: "pending" },
    ];
  }
  if (lower.includes("speaker") || lower.includes("bio") || lower.includes("blurb") || lower.includes("copy")) {
    return [
      { id: "content", label: "Writing publish-ready copy", status: "running" },
      { id: "save", label: "Saving to event modules", status: "pending" },
    ];
  }
  if (lower.includes("brochure") || lower.includes("import agenda") || lower.includes("paste")) {
    return [
      { id: "parse", label: "Parsing event brief", status: "running" },
      { id: "map", label: "Mapping into event & schedule", status: "pending" },
    ];
  }
  if (lower.includes("attendee") || lower.includes("registered") || lower.includes("check-in")) {
    return [
      { id: "stats", label: "Checking registration stats", status: "running" },
      { id: "summary", label: "Preparing summary", status: "pending" },
    ];
  }
  if (lower.includes("landing") || lower.includes("website") || lower.includes("publish")) {
    return [
      { id: "page", label: "Reviewing landing page", status: "running" },
      { id: "save", label: "Applying changes", status: "pending" },
    ];
  }
  if (lower.includes("create everything") || lower.includes("set up everything") || lower.includes("summit")) {
    return [
      { id: "plan", label: "Planning event setup workflow", status: "running", detail: "Ordering registration → website → EDM" },
      { id: "execute", label: "Configuring registration, website & comms", status: "pending" },
    ];
  }
  return DEFAULT_BUSY_STEPS;
}

function useStreamedBusySteps(busy, busySteps) {
  const source = busySteps?.length ? busySteps : DEFAULT_BUSY_STEPS;
  const [visible, setVisible] = useState([]);

  useEffect(() => {
    if (!busy) {
      setVisible([]);
      return undefined;
    }
    setVisible([{ ...source[0], status: "running" }]);
    const timers = [];
    source.forEach((step, idx) => {
      if (idx === 0) return;
      timers.push(
        setTimeout(() => {
          setVisible((prev) => {
            const donePrev = prev.map((s, i) =>
              i === prev.length - 1 ? { ...s, status: "done" } : s,
            );
            return [...donePrev, { ...step, status: "running" }];
          });
        }, 650 * idx),
      );
    });
    return () => timers.forEach(clearTimeout);
  }, [busy, JSON.stringify(source.map((s) => s.id || s.label))]);

  return visible;
}

function useRevealSteps(steps, enabled) {
  const [count, setCount] = useState(enabled ? 0 : steps.length);

  useEffect(() => {
    if (!enabled || !steps.length) {
      setCount(steps.length);
      return undefined;
    }
    setCount(0);
    const timers = [];
    steps.forEach((_, idx) => {
      timers.push(setTimeout(() => setCount(idx + 1), 180 + idx * 220));
    });
    return () => timers.forEach(clearTimeout);
  }, [enabled, JSON.stringify(steps.map((s) => s.id || s.label))]);

  return steps.slice(0, Math.max(count, enabled ? 0 : steps.length));
}

export default function ThinkingProcess({
  steps = [],
  busy = false,
  busySteps = null,
  defaultOpen = true,
  streamReveal = true,
  confidence = null,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const streamedBusy = useStreamedBusySteps(busy, busySteps);
  const finalSteps = useMemo(() => (steps.length ? steps : []), [steps]);
  const revealed = useRevealSteps(finalSteps, streamReveal && !busy && finalSteps.length > 0);
  const visible = busy ? streamedBusy : revealed;

  if (!visible.length && !busy) return null;

  const doneCount = visible.filter((s) => s.status === "done").length;
  const title = busy
    ? "Thinking"
    : doneCount
      ? `Thought · ${doneCount} step${doneCount === 1 ? "" : "s"}`
      : "Thinking process";
  const confPct =
    confidence && typeof confidence.score === "number"
      ? Math.round(Number(confidence.score) * 100)
      : null;

  return (
    <div
      className="rounded-xl border border-[var(--border-default)] bg-[color-mix(in_srgb,var(--bg-elev)_92%,var(--primary)_8%)] p-3 mb-3"
      data-testid="thinking-process"
    >
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-[var(--text-tertiary)]">
          {busy ? <Loader2 size={12} className="animate-spin text-[var(--primary)]" /> : null}
          {title}
          {confPct != null && !busy ? (
            <span className="normal-case tracking-normal text-[var(--text-secondary)]">
              · {confPct}% {confidence?.label || ""}
            </span>
          ) : null}
        </span>
        {open ? (
          <ChevronDown size={14} className="text-[var(--text-tertiary)]" />
        ) : (
          <ChevronRight size={14} className="text-[var(--text-tertiary)]" />
        )}
      </button>
      {open ? (
        <ol className="mt-2 space-y-2">
          {visible.map((step, idx) => {
            const status = step.status || (busy && idx === visible.length - 1 ? "running" : "done");
            const Icon = STATUS_ICON[status] || Circle;
            return (
              <li
                key={step.id || `${step.label}-${idx}`}
                className="text-xs animate-in fade-in slide-in-from-top-1 duration-300"
                style={{ animationDelay: `${idx * 40}ms` }}
              >
                <div className="flex items-start gap-2">
                  <Icon size={14} className={`shrink-0 mt-0.5 ${STATUS_CLASS[status] || STATUS_CLASS.pending}`} />
                  <div className="min-w-0">
                    <div className={status === "done" ? "text-[var(--text-secondary)]" : "text-[var(--text-primary)]"}>
                      {step.label}
                      {status === "running" ? (
                        <span className="inline-block ml-1 w-1.5 h-3 align-middle bg-[var(--primary)] animate-pulse" />
                      ) : null}
                    </div>
                    {step.detail ? (
                      <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5 leading-snug whitespace-pre-wrap">
                        {step.detail}
                      </div>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
          {busy ? (
            <li className="text-[10px] text-[var(--text-tertiary)] font-mono pl-5 pt-0.5">
              Working…
            </li>
          ) : null}
        </ol>
      ) : null}
    </div>
  );
}
